import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DatasetMergeTableProps } from '@databox/ember-integrations/lib/components/Datasets/DatasetMergeTable/DatasetMergeTable';
import DatasetMergeTableEntryPoint from '@databox/ember-integrations/lib/components/Datasets/DatasetMergeTable/DatasetMergeTableEntryPoint';

import type { LineageVerifyTarget, ReactIntegrations, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode, isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BillingDatasetsComponent from 'webapp/components/billing/datasets';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import NoResultsMergeDatasetsComponent from 'webapp/components/no-results/merge-datasets';

import type { SourceType } from 'webapp/models-custom';
import { getBillingFeature } from 'webapp/models/helpers/react-model-mapper';

type Args = Record<string, never>;

export default class DataManagerMergedDatasetsComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('react') declare reactService: Service.React;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;
	invalidatedGetMetricQuery: (() => void) | null = null;

	@tracked isEmptyState = false;

	addButtonText = 'Merge datasets';

	get showBillingPage(): boolean {
		return !this.billingService.advancedDataSourcesEnabled;
	}

	get showDatasetEmptyPage(): boolean {
		return this.isEmptyState;
	}

	get addButtonContainerNode() {
		return isAlive(this) ? this.navigationService.addButtonContainerNode : null;
	}

	@action
	setEmptyState(empty: boolean) {
		this.isEmptyState = empty;
	}

	@action
	startTrial(): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		this.billingService.handleLimitations(BillingFeature.ADVANCED_DATA_SOURCES, 'datasets');
	}

	@action
	createMergedDataset(): void {
		this.navigationService.transitionToRoute('datamanager.create-new-merge');
	}

	@action
	handleOnNavigateToMetrics(spaceAccessId: number, sourceTypeKey?: string) {
		if (sourceTypeKey) {
			this.navigationService.transitionToRoute(
				'metric-library.source.specific',
				sourceTypeKey,
				spaceAccessId,
			);
		} else {
			this.navigationService.transitionToRoute('metric-library');
		}
	}

	@action
	handleEditDatasetSetup(spaceAccessId: number, dataSourceType: { key: string }) {
		this.connectService.editDatasetSetup(dataSourceType.key, {
			sourceId: spaceAccessId,
		});
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		this.renderDatasets();
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderDatasets);
	}

	renderDatasets() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: DatasetMergeTableProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			isWhiteLabeled: this.accountService.whiteLabelingEnabled,
			onViewDataset: (spaceAccessId: number) => {
				this.datasetEventsService.handleViewDataset(`${spaceAccessId}`);
			},
			onViewMetric: (metricKey: string) =>
				this.datasetEventsService.handleViewMetricInsights(metricKey),
			onViewDataSource: () => this.datasetEventsService.handleViewDataSources(),
			onLineageNavigate: (kind: string, id: string, intent?: 'view' | 'edit') =>
				this.datasetEventsService.handleLineageNavigate(kind, id, intent),
			onLineageVerify: (target: LineageVerifyTarget) =>
				this.datasetEventsService.handleLineageVerify(target),
			onEditDataset: (id: number) => {
				this.datasetEventsService.handleEditDataset(`${id}`);
			},
			onNavigateToMetrics: (spaceAccessId: number, sourceType: SourceType) =>
				this.handleOnNavigateToMetrics(spaceAccessId, sourceType?.key),
			onEmptyTable: this.setEmptyState,
			onAddDataset: () => {
				/* nothing */
			},
			onExportError: this.datasetEventsService.handleExportError,
			onDataExport: this.datasetEventsService.handleDataExport,
			onDatasetDeleted: this.datasetEventsService.handleDatasetDeleted,
			onDataRefresh: this.datasetEventsService.handleDataRefresh,
			onCreateMetrics: (id: number) =>
				this.datasetEventsService.handleMetricCreate(
					`${id}`,
					this.invalidatedGetMetricQuery,
				),
			onValidationError: this.datasetEventsService.handleValidationError,
			onDeleteSourceError: this.datasetEventsService.handleDeleteSourceError,
			onEditDatasetSetup: this.handleEditDatasetSetup,
			onDuplicateDataSourceError: this.datasetEventsService.handleDuplicateDataSourceError,
			onSourceDuplicated: this.datasetEventsService.handleSourceDuplicated,
			getInvalidatedGetMetricQuery: (invalidatedGetMetricQuery) => {
				this.invalidatedGetMetricQuery = invalidatedGetMetricQuery;
			},
			onConnectionUpdated: this.datasetEventsService.handleOnConnectionUpdated,
			onAddMergeDataset: this.createMergedDataset,
			onApiError: (error: ReactIntegrations.Error) =>
				this.datasetEventsService.handleApiError(error),
			onDatasetFinishSetupSuccess: this.datasetEventsService.handleDatasetFinishSetupSuccess,
			onOpenActivityLog: this.datasetEventsService.openActivityLogCallback,
			onPlanUpgrade: async (featureName: string) => {
				return await this.billingService.handlePlanUpgradeForFeature(
					getBillingFeature(featureName),
				);
			},
			onVerificationToggle: (verificationInfo: Record<string, unknown>, objectName: string) =>
				this.datasetEventsService.handleVerificationToggle(verificationInfo, objectName),
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				DatasetMergeTableEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);

		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		{{#if this.showBillingPage}}
			<BillingDatasetsComponent @onCreate={{this.startTrial}} />
		{{else if this.showDatasetEmptyPage}}
			<NoResultsMergeDatasetsComponent @onCreate={{this.createMergedDataset}} />
		{{else}}
			<div
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
				id="datasets-table-container"
			/>

			{{#if this.addButtonContainerNode}}
				{{#in-element this.addButtonContainerNode}}
					<CommonAddButtonComponent
						@text={{this.addButtonText}}
						@callTrigger={{this.createMergedDataset}}
						@icon="Merge"
					/>
				{{/in-element}}
			{{/if}}
		{{/if}}
	</template>
}
